/**
 * ユーザープロフィール写真の共通表示 (initiative: user-profile-avatar-20260828)。
 *
 * 写真が無い / URL が壊れている場合の fallback は 2 段:
 *   1. `initialsSeed` が渡されていれば **氏名由来のイニシャル円** (opt-in)
 *   2. それ以外は **既存の Heroicons user-circle**
 * ヘッダーとユーザー編集モーダルの両方で同じ fallback 規律を使うために切り出している
 * (片方だけ壊れた表示になる、という差を作らないため)。
 *
 * イニシャル段は case-user-identity-display-avatar-20260830 で案件メモ族向けに追加した
 * **additive な opt-in** で、`initialsSeed` を渡さない既存 2 箇所の描画は一切変わらない。
 *
 * presigned GET URL には TTL があるため、長時間開いたままのタブでは読み込みに失敗しうる。
 * その場合も broken image を出さず、静かにアイコンへ落とす。
 */
import { useEffect, useState } from 'react';

interface UserAvatarProps {
  /** 表示用 URL (BE が presign したもの)。未設定・解決失敗時は null。 */
  avatarUrl?: string | null;
  /** 円の直径を決める Tailwind クラス (例: `h-6 w-6`)。 */
  sizeClass: string;
  /** スクリーンリーダー向けの説明。装飾用途なら空文字を渡す。 */
  alt?: string;
  /** 追加クラス (枠線など)。 */
  className?: string;
  /**
   * 写真が無い / 読み込めないときに出す **氏名由来イニシャル**の元になる表示名
   * (opt-in。initiative: case-user-identity-display-avatar-20260830)。
   *
   * **渡さなければ従来どおり人物アイコンへ落ちる** — 既存の呼び出し (ヘッダー /
   * ユーザー編集モーダル) の見た目を変えないため、既定では有効にしない。
   *
   * **email をそのまま渡してはならない** — 渡すと `e` のような
   * メール先頭 1 文字が出て、本 initiative が直そうとしている表示そのものになる。
   * 呼び出し側で氏名へ解決してから渡すこと。
   */
  initialsSeed?: string | null;
  /** イニシャル円の配色クラス (未指定なら brand 既定色)。 */
  initialsClassName?: string;
}

/** 表示名から出すイニシャル 1 文字。空白のみ / 未設定は空文字 (記号で代用しない)。 */
function initialOf(seed: string | null | undefined): string {
  const trimmed = (seed ?? '').trim();
  return trimmed ? trimmed.slice(0, 1) : '';
}

export default function UserAvatar({
  avatarUrl,
  sizeClass,
  alt = '',
  className = '',
  initialsSeed,
  initialsClassName = 'bg-indigo-100 font-semibold text-indigo-700 dark:bg-indigo-500/20 dark:text-indigo-300',
}: UserAvatarProps) {
  // 読み込みに失敗した URL を覚えておき、同じ URL で再試行し続けないようにする。
  const [failed, setFailed] = useState(false);

  // URL が差し替わったら失敗状態をリセットする (写真を変更した直後に古い失敗を引きずらない)。
  useEffect(() => {
    setFailed(false);
  }, [avatarUrl]);

  if (avatarUrl && !failed) {
    return (
      <img
        src={avatarUrl}
        alt={alt}
        aria-hidden={alt ? undefined : true}
        onError={() => setFailed(true)}
        className={`${sizeClass} shrink-0 rounded-full object-cover ${className}`}
      />
    );
  }

  // 写真が無い / 壊れているときは、氏名を渡された場合に限りイニシャル円を出す。
  // 画像読み込み失敗 (`failed`) でもここへ落ちるので、broken image は決して見せない。
  const initial = initialOf(initialsSeed);
  if (initial) {
    return (
      <span
        role={alt ? 'img' : undefined}
        aria-label={alt || undefined}
        aria-hidden={alt ? undefined : true}
        className={`inline-flex ${sizeClass} shrink-0 select-none items-center justify-center rounded-full ${initialsClassName} ${className}`}
      >
        {initial}
      </span>
    );
  }

  return (
    /* Heroicons: user-circle — 写真未設定時の既定表示 (従来の表示と同一) */
    <svg
      xmlns="http://www.w3.org/2000/svg"
      fill="none"
      viewBox="0 0 24 24"
      strokeWidth={2}
      stroke="currentColor"
      className={`${sizeClass} shrink-0 ${className}`}
      role={alt ? 'img' : undefined}
      aria-label={alt || undefined}
      aria-hidden={alt ? undefined : true}
    >
      <path
        strokeLinecap="round"
        strokeLinejoin="round"
        d="M17.982 18.725A7.488 7.488 0 0 0 12 15.75a7.488 7.488 0 0 0-5.982 2.975m11.963 0a9 9 0 1 0-11.963 0m11.963 0A8.966 8.966 0 0 1 12 21a8.966 8.966 0 0 1-5.982-2.275M15 9.75a3 3 0 1 1-6 0 3 3 0 0 1 6 0Z"
      />
    </svg>
  );
}
